package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.SuccessResponse
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.ConfirmableButton
import com.sddamico.components.widgets.ErrorCard
import com.sddamico.components.widgets.PageLoading
import com.sddamico.models.AddOwnerRequest
import com.sddamico.models.DeleteOwnerRequest
import com.sddamico.models.ManagedClubListResponse
import com.sddamico.models.ManagedClubResponse
import com.sddamico.models.OwnerResponse
import com.sddamico.models.UpdateClubRequest
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initManageClubs(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Manage Clubs"))
}

@Page("manage-clubs")
@Layout(".components.layouts.PageLayout")
@Composable
fun ManageClubsPage() {
    val authState = LocalAuthState.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    // Auth guard — use realUserRole during impersonation so admin retains access
    val effectiveRole = authState.user?.realUserRole ?: authState.user?.role
    LaunchedEffect(authState.loading, authState.user) {
        if (!authState.loading && (authState.user == null || (effectiveRole != "LEAGUE_MANAGER" && effectiveRole != "LEAGUE_ADMIN"))) {
            ctx.router.tryRoutingTo("/")
        }
    }

    if (authState.loading || authState.user == null || (effectiveRole != "LEAGUE_MANAGER" && effectiveRole != "LEAGUE_ADMIN")) {
        PageLoading()
        return
    }

    var clubs by remember { mutableStateOf<List<ManagedClubResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var loadError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        loading = true
        try {
            val response = apiGet("/api/admin/manage-clubs", ManagedClubListResponse.serializer())
            clubs = response.clubs
        } catch (e: ApiException) {
            loadError = e.errorMessage
        } catch (_: Exception) {
            loadError = "Failed to load clubs"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    loadError?.let {
        ErrorCard(it)
        return
    }

    // Main card
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(1.5.cssRem)
            .gap(1.25.cssRem)
    ) {
        // Heading
        SpanText(
            "Manage Clubs",
            Modifier
                .fontFamily(*HeadingFontFamily)
                .fontSize(1.25.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(SitePalette.text)
        )

        // Divider
        Div(
            Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(SitePalette.border)
                .toAttrs()
        )

        // Club sections
        clubs.forEachIndexed { index, club ->
            ClubEditorSection(
                original = club,
                scope = scope,
                onOwnerAdded = { newOwner ->
                    clubs = clubs.map {
                        if (it.clubId == club.clubId) it.copy(owners = it.owners + newOwner)
                        else it
                    }
                },
                onOwnerDeleted = { ownerId ->
                    clubs = clubs.map {
                        if (it.clubId == club.clubId) it.copy(owners = it.owners.filter { o -> o.id != ownerId })
                        else it
                    }
                },
            )

            // Divider between clubs (not after the last one)
            if (index < clubs.lastIndex) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(SitePalette.border)
                        .toAttrs()
                )
            }
        }
    }
}

@Composable
private fun ClubEditorSection(
    original: ManagedClubResponse,
    scope: kotlinx.coroutines.CoroutineScope,
    onOwnerAdded: (OwnerResponse) -> Unit,
    onOwnerDeleted: (String) -> Unit,
) {
    var clubName by remember(original.clubId, original.clubName) { mutableStateOf(original.clubName) }
    var savedClubName by remember(original.clubId, original.clubName) { mutableStateOf(original.clubName) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var deletingOwnerId by remember { mutableStateOf<String?>(null) }

    // Add owner state
    var addingOwner by remember { mutableStateOf(false) }
    var newOwnerName by remember { mutableStateOf("") }
    var newOwnerEmail by remember { mutableStateOf("") }
    var savingNewOwner by remember { mutableStateOf(false) }
    var addOwnerError by remember { mutableStateOf<String?>(null) }

    // Track per-owner edited state
    val ownerNames = remember(original.clubId, original.owners) {
        mutableStateMapOf<String, String>().apply {
            original.owners.forEach { put(it.id, it.name) }
        }
    }
    val ownerEmails = remember(original.clubId, original.owners) {
        mutableStateMapOf<String, String>().apply {
            original.owners.forEach { put(it.id, it.email) }
        }
    }
    val savedOwners = remember(original.clubId, original.owners) {
        mutableStateMapOf<String, OwnerResponse>().apply {
            original.owners.forEach { put(it.id, it) }
        }
    }

    val clubNameChanged = clubName != savedClubName
    val ownersChanged = original.owners.any { owner ->
        ownerNames[owner.id] != savedOwners[owner.id]?.name ||
            ownerEmails[owner.id] != savedOwners[owner.id]?.email
    }
    val hasChanges = clubNameChanged || ownersChanged
    val allValid = clubName.isNotBlank() &&
        original.owners.all { ownerNames[it.id]?.isNotBlank() == true && ownerEmails[it.id]?.isNotBlank() == true }
    val canSave = hasChanges && allValid && !saving

    Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
        // Club name field
        LabeledInput(
            label = "Club Name",
            value = clubName,
            onValueChange = { clubName = it },
        )

        // Existing owners
        original.owners.forEachIndexed { index, owner ->
            val ownerLabel = if (original.owners.size > 1) "Owner ${index + 1}" else "Owner"

            LabeledInput(
                label = "$ownerLabel Name",
                value = ownerNames[owner.id] ?: owner.name,
                onValueChange = { ownerNames[owner.id] = it },
            )

            LabeledInput(
                label = "$ownerLabel Email",
                value = ownerEmails[owner.id] ?: owner.email,
                onValueChange = { ownerEmails[owner.id] = it },
                inputType = InputType.Email,
            )

            // Delete Owner button
            Row(
                Modifier.fillMaxWidth().position(Position.Relative),
                verticalAlignment = Alignment.CenterVertically
            ) {
                ConfirmableButton(
                    label = "Delete Owner",
                    loadingLabel = "Deleting...",
                    confirmLabel = "Delete ${ownerNames[owner.id] ?: owner.name}? This cannot be undone.",
                    isLoading = deletingOwnerId == owner.id,
                    enabled = deletingOwnerId == null,
                    buttonModifier = Modifier
                        .backgroundColor(SitePalette.error)
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .padding(topBottom = 0.25.cssRem, leftRight = 0.625.cssRem)
                        .fontSize(0.6875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .opacity(if (deletingOwnerId == null) 0.8 else 0.4),
                    onConfirm = {
                        scope.launch {
                            deletingOwnerId = owner.id
                            error = null
                            try {
                                val body = apiJson.encodeToString(DeleteOwnerRequest(
                                    clubId = original.clubId,
                                    ownerId = owner.id,
                                ))
                                apiPost("/api/admin/delete-owner", body, SuccessResponse.serializer())
                                onOwnerDeleted(owner.id)
                            } catch (e: ApiException) {
                                error = e.errorMessage
                            } catch (_: Exception) {
                                error = "Failed to delete owner"
                            }
                            deletingOwnerId = null
                        }
                    }
                )
            }
        }

        // New owner fields (visible when addingOwner is true)
        if (addingOwner) {
            val newOwnerLabel = if (original.owners.isEmpty()) "Owner" else "New Owner"

            LabeledInput(
                label = "$newOwnerLabel Name",
                value = newOwnerName,
                onValueChange = { newOwnerName = it },
            )

            LabeledInput(
                label = "$newOwnerLabel Email",
                value = newOwnerEmail,
                onValueChange = { newOwnerEmail = it },
                inputType = InputType.Email,
            )

            addOwnerError?.let {
                SpanText(
                    it,
                    Modifier
                        .color(SitePalette.error)
                        .fontSize(0.8125.cssRem)
                )
            }
        }

        // Error message
        error?.let {
            SpanText(
                it,
                Modifier
                    .color(SitePalette.error)
                    .fontSize(0.8125.cssRem)
            )
        }

        // Button row
        Row(
            Modifier
                .fillMaxWidth()
                .position(Position.Relative)
                .gap(0.5.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Save button (for existing club/owner edits)
            if (!addingOwner) {
                ConfirmableButton(
                    label = "Save",
                    loadingLabel = "Saving...",
                    confirmLabel = "Save changes to this club?",
                    isLoading = saving,
                    enabled = canSave,
                    buttonModifier = Modifier
                        .backgroundColor(if (canSave) SitePalette.primary else SitePalette.border)
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.625.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.8125.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .opacity(if (canSave) 1.0 else 0.5),
                    onConfirm = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                // Save club name if changed
                                if (clubNameChanged) {
                                    val body = apiJson.encodeToString(UpdateClubRequest(
                                        clubId = original.clubId,
                                        clubName = clubName.trim(),
                                    ))
                                    apiPost("/api/admin/update-club", body, SuccessResponse.serializer())
                                    savedClubName = clubName.trim()
                                }
                                // Save each changed owner
                                for (owner in original.owners) {
                                    val nameChanged = ownerNames[owner.id] != savedOwners[owner.id]?.name
                                    val emailChanged = ownerEmails[owner.id] != savedOwners[owner.id]?.email
                                    if (nameChanged || emailChanged) {
                                        val body = apiJson.encodeToString(UpdateClubRequest(
                                            clubId = original.clubId,
                                            ownerId = owner.id,
                                            ownerName = if (nameChanged) ownerNames[owner.id]?.trim() else null,
                                            ownerEmail = if (emailChanged) ownerEmails[owner.id]?.trim() else null,
                                        ))
                                        apiPost("/api/admin/update-club", body, SuccessResponse.serializer())
                                        savedOwners[owner.id] = OwnerResponse(
                                            id = owner.id,
                                            name = ownerNames[owner.id]?.trim() ?: owner.name,
                                            email = ownerEmails[owner.id]?.trim() ?: owner.email,
                                        )
                                    }
                                }
                            } catch (e: ApiException) {
                                error = e.errorMessage
                            } catch (_: Exception) {
                                error = "Failed to update club"
                            }
                            saving = false
                        }
                    }
                )
            }

            // Save new owner button (when adding)
            if (addingOwner) {
                val newOwnerValid = newOwnerName.isNotBlank() && newOwnerEmail.isNotBlank()
                Div(
                    Modifier
                        .backgroundColor(if (newOwnerValid && !savingNewOwner) SitePalette.primary else SitePalette.border)
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.625.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.8125.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .opacity(if (newOwnerValid && !savingNewOwner) 1.0 else 0.5)
                        .cursor(if (newOwnerValid && !savingNewOwner) Cursor.Pointer else Cursor.Default)
                        .onClick {
                            if (!newOwnerValid || savingNewOwner) return@onClick
                            scope.launch {
                                savingNewOwner = true
                                addOwnerError = null
                                try {
                                    val body = apiJson.encodeToString(AddOwnerRequest(
                                        clubId = original.clubId,
                                        ownerName = newOwnerName.trim(),
                                        ownerEmail = newOwnerEmail.trim(),
                                    ))
                                    val newOwner = apiPost("/api/admin/add-owner", body, OwnerResponse.serializer())
                                    onOwnerAdded(newOwner)
                                    // Reset add owner state
                                    addingOwner = false
                                    newOwnerName = ""
                                    newOwnerEmail = ""
                                } catch (e: ApiException) {
                                    addOwnerError = e.errorMessage
                                } catch (_: Exception) {
                                    addOwnerError = "Failed to add owner"
                                }
                                savingNewOwner = false
                            }
                        }
                        .toAttrs()
                ) {
                    Text(if (savingNewOwner) "Saving..." else "Save")
                }
            }

            // Add Owner / Cancel button
            Div(
                Modifier
                    .backgroundColor(if (addingOwner) SitePalette.error else SitePalette.border)
                    .color(Colors.White)
                    .borderRadius(8.px)
                    .padding(topBottom = 0.625.cssRem, leftRight = 1.cssRem)
                    .fontSize(0.8125.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        if (savingNewOwner) return@onClick
                        addingOwner = !addingOwner
                        if (!addingOwner) {
                            newOwnerName = ""
                            newOwnerEmail = ""
                            addOwnerError = null
                        }
                    }
                    .toAttrs()
            ) {
                Text(if (addingOwner) "Cancel" else "Add Owner")
            }
        }
    }
}

@Composable
private fun LabeledInput(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    inputType: InputType<String> = InputType.Text,
) {
    Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
        SpanText(
            label,
            Modifier
                .fontSize(0.8125.cssRem)
                .fontWeight(FontWeight.Medium)
                .color(SitePalette.textSecondary)
        )
        Input(inputType) {
            value(value)
            onInput { onValueChange(it.value) }
            style {
                property("width", "100%")
                property("box-sizing", "border-box")
                property("padding", "0.75rem")
                property("background-color", "#0F1216")
                property("border", "1px solid #2A3038")
                property("border-radius", "8px")
                property("color", "#E8E6E1")
                property("font-size", "0.9375rem")
                property("outline", "none")
            }
        }
    }
}
